// IgnoreAlarmModal.js — Ignore alarm confirmation modal (PRD-48)
// Standalone: usable from both DriftAlarmDrawer and AssetDataSections.
//
// PRD-63 WP3: dual-route on submit, same pattern as AcceptAlarmModal. See that
// file's header comment for full rationale.

import React, { useState } from 'react';
import { ignoreAlarm } from '../services/driftService';
import { createSuppression } from '../services/suppressionService';

function IgnoreAlarmModal({ isOpen, onClose, alarm, finding, assetId, onSuccess, defaultScope = 'host' }) {
    const [reason, setReason] = useState('');
    const [scope, setScope] = useState(defaultScope);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Default expiration: 5 years from now
    const defaultExpiry = new Date(Date.now() + 5 * 365 * 86400000).toISOString().split('T')[0];
    const [expiresAt, setExpiresAt] = useState(defaultExpiry);

    if (!isOpen) return null;

    const alarmId = alarm?.alarmId || finding?.alarmId;
    const displayName = alarm?.name || finding?.name || finding?.findingName || 'this alarm';

    const handleSubmit = async () => {
        if (reason.trim().length < 10) {
            setError('Reason must be at least 10 characters.');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            let result;
            const expiresAtIso = new Date(expiresAt).toISOString();
            if (alarmId) {
                result = await ignoreAlarm(alarmId, {
                    reason: reason.trim(),
                    expiresAt: expiresAtIso,
                    scope,
                });
            } else {
                // Below-threshold finding — see AcceptAlarmModal header note.
                result = await createSuppression({
                    assetId: assetId || finding?.assetId,
                    findingType: finding?.findingType || 'vulnerability',
                    findingIdentifier: finding?.findingIdentifier || finding?.templateId
                        || finding?.cve || finding?.cveId || finding?.id || finding?.name,
                    findingName: finding?.findingName || finding?.name || finding?.title || 'Unknown',
                    findingId: finding?.findingId || null,
                    matchedAt: finding?.matchedAt || finding?.host || finding?.url || null,
                    severity: finding?.severity || null,
                    reason: reason.trim(),
                    disposition: 'ignore',
                    expiresAt: expiresAtIso,
                    scope,
                });
            }
            onSuccess && onSuccess(result);
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to ignore alarm');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="alarm-modal-backdrop" onClick={onClose}>
            <div className="alarm-modal" onClick={e => e.stopPropagation()}>
                <div className="alarm-modal__header">
                    <h3>Ignore Alarm</h3>
                    <button className="alarm-modal__close" onClick={onClose}>×</button>
                </div>
                <div className="alarm-modal__body">
                    <p className="alarm-modal__desc">
                        Ignoring <strong>{displayName}</strong> marks it as a false positive.
                        The finding will be <strong>removed from your risk score</strong> and hidden from alarms until the expiration date.
                        Use this only when you are certain the detection is wrong.
                    </p>

                    <div className="alarm-modal__field">
                        <label>Expires on</label>
                        <input
                            type="date"
                            className="alarm-modal__input"
                            value={expiresAt}
                            onChange={e => setExpiresAt(e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                        />
                    </div>

                    <div className="alarm-modal__field">
                        <label>Scope</label>
                        <div className="alarm-modal__pills">
                            <button
                                className={`alarm-modal__pill ${scope === 'host' ? 'active' : ''}`}
                                onClick={() => setScope('host')}
                                type="button"
                            >
                                This host only
                            </button>
                            <button
                                className={`alarm-modal__pill ${scope === 'asset' ? 'active' : ''}`}
                                onClick={() => setScope('asset')}
                                type="button"
                            >
                                Entire asset
                            </button>
                        </div>
                    </div>

                    <div className="alarm-modal__field">
                        <label>Reason <span className="alarm-modal__hint">(min 10 chars)</span></label>
                        <textarea
                            className="alarm-modal__textarea"
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            placeholder="e.g., Redirect port misidentified as risky service"
                            rows={3}
                        />
                    </div>

                    {error && <div className="alarm-modal__error">{error}</div>}
                </div>
                <div className="alarm-modal__footer">
                    {reason.trim().length < 10 && reason.trim().length > 0 && (
                        <span className="alarm-modal__footer-hint">
                            {10 - reason.trim().length} more character{10 - reason.trim().length !== 1 ? 's' : ''} needed
                        </span>
                    )}
                    <button className="alarm-modal__btn alarm-modal__btn--cancel" onClick={onClose} disabled={loading}>
                        Cancel
                    </button>
                    <button
                        className="alarm-modal__btn alarm-modal__btn--ignore"
                        onClick={reason.trim().length < 10 ? () => setError('Please enter a reason (at least 10 characters) to ignore this alarm.') : handleSubmit}
                        disabled={loading}
                    >
                        {loading ? 'Ignoring...' : 'Ignore'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default IgnoreAlarmModal;
